import { ReactNode, SyntheticEvent, useEffect, useState } from "react";
import { useRouter } from "next/router";
import styled from "@emotion/styled";
import { Tab, Tabs } from "@mui/material";

import { ActionButton, TabPanel } from "@/components";
import { useCustomTranslation } from "@/utils/useCustomTranslation";

interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
  showAction?: boolean;
}

interface TabGroupProps {
  tabs: TabItem[];
  className?: string;
  onActionClick?: () => void;
}

export const TabGroup = ({ tabs, className, onActionClick }: TabGroupProps) => {
  const { t } = useCustomTranslation();
  const [value, setValue] = useState(0);

  const router = useRouter();
  useEffect(() => {
    const inquiryId = router.query.inquiryId;
    // console.log(inquiryId);
    inquiryId ? setValue(1) : setValue(0);
  }, [router]);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <TabContainer className={className}>
      <TabHeader>
        <StyledTabs
          value={value}
          onChange={handleChange}
          orientation="horizontal"
          TabIndicatorProps={{ style: { display: "none" } }}
        >
          {tabs.map(({ id, label, disabled }) => (
            <StyledTab
              key={id}
              label={label}
              disabled={disabled}
              iconPosition="start"
            />
          ))}
        </StyledTabs>
        {tabs[value]?.showAction && (
          <ActionButton
            variant="contained"
            onClick={onActionClick}
          >
            {t("write")}
          </ActionButton>
        )}
      </TabHeader>
      {tabs.map(({ id, content }, index) => (
        <TabPanel
          key={id}
          value={value}
          index={index}
        >
          {content}
        </TabPanel>
      ))}
    </TabContainer>
  );
};

const TabContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTabs = styled(Tabs)`
  && {
    min-height: auto;
    border-bottom: none;

    .MuiTabs-flexContainer {
      gap: 8px;
    }
  }
`;

const StyledTab = styled(Tab)(({ theme }) => ({
  "&&": {
    fontSize: 14,
    fontWeight: 500,
    textTransform: "none",
    minWidth: 83,
    minHeight: 36,
    padding: "8px 16px",
    borderRadius: 8,
    color: "#666666",
    backgroundColor: theme.colors.environment.background.default,
    border: "1px solid transparent",
    letterSpacing: -0.4,

    "&.Mui-selected": {
      color: theme.colors.sub.blue.default,
      border: `1px solid ${theme.colors.sub.blue.default}`,
      fontWeight: 700,
    },

    "&:hover": {
      backgroundColor: theme.colors.sub.blue.hover,
      color: theme.colors.brand.white.default,
    },

    "&.Mui-selected:hover": {
      backgroundColor: theme.colors.environment.background.default,
      color: theme.colors.sub.blue.default,
    },
  },
}));
